import {
  getDefaultHelloSignTree,
  getHelloSignField,
} from 'app/shared/components/forms/FormEditor/signature/Signature.utils';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

const selectFormTemplateState = ({ form }) => form;

export const selectFormTemplate = createSelector(selectFormTemplateState, ({ data }) => data);

export const selectPreviewingFormTemplate = createSelector(
  selectFormTemplateState,
  ({ previewingTemplate }) => previewingTemplate,
);

export const selectIsElementsChanged = createSelector(
  selectFormTemplateState,
  ({ isElementsChanged }) => isElementsChanged,
);

const selectPdfFormTemplateElements = createSelector(selectFormTemplate, form =>
  _.get(form.pdfMetadata, 'elements', []),
);

const selectPdfFormTemplateDependenceGroups = createSelector(selectFormTemplate, form =>
  _.get(form.pdfMetadata, 'dependenceGroups', []),
);

export const selectPdfFormTemplateDependenceGroupsById = createSelector(
  selectPdfFormTemplateDependenceGroups,
  dependenceGroups => _.keyBy(dependenceGroups, 'id'),
);

export const selectPdfFormHelloSignTree = memoizeOne(rootDependenceGroupId =>
  createSelector(
    [selectPdfFormTemplateElements, selectPdfFormTemplateDependenceGroups],
    (allElements, dependenceGroups) => {
      if (!rootDependenceGroupId) {
        return getDefaultHelloSignTree();
      }

      const rootDependenceGroup = dependenceGroups.find(({ id }) => id === rootDependenceGroupId);
      const elements = allElements.map(({ helloSignTag }) => helloSignTag).filter(Boolean);
      const dependenceGroupsByParentId = _.keyBy(dependenceGroups, 'parentId');

      const helloSignTree = elements.reduce(
        (acc, helloSignTag) => {
          if (!helloSignTag) {
            return acc;
          }

          const { dependenceGroupId, id } = helloSignTag;

          const parentDependenceGroupId = dependenceGroupsByParentId[id]?.id;

          if (dependenceGroupId === rootDependenceGroupId) {
            const children = elements
              .filter(helloSignTag => helloSignTag.dependenceGroupId === parentDependenceGroupId)
              .map(helloSignTag => getHelloSignField({ helloSignTag, rootDependenceGroupId }));

            acc.parents.push({
              ...getHelloSignField({ helloSignTag, isParent: true, rootDependenceGroupId }),
              children,
            });
          }

          acc.dependenceGroupRequirement = rootDependenceGroup.requirement?.type?.toLowerCase();

          return acc;
        },
        {
          parents: [],
          dependenceGroupRequirement: null,
        },
      );

      return helloSignTree;
    },
  ),
);

export const selectPdfFormPageExtendedElements = memoizeOne((selectedPage, scale) =>
  createSelector([selectPdfFormTemplateElements], elements =>
    elements.reduce((acc, element, index) => {
      if (element.position.page === selectedPage) {
        acc.push({
          ...element,
          index,
          position: {
            ...element.position,
            height: element.position.height * scale,
            width: element.position.width * scale,
            x: element.position.x * scale,
            y: element.position.y * scale,
          },
        });
      }

      return acc;
    }, []),
  ),
);

export const selectFormNavigationStateType = createSelector(
  selectFormTemplateState,
  ({ navigationStateType }) => navigationStateType,
);

export const selectFormNavigationType = createSelector(
  selectFormTemplateState,
  ({ navigationType }) => navigationType,
);

export const selectFormType = createSelector(
  selectFormTemplate,
  formTemplate => formTemplate?.formType,
);

export const selectFormApplicationType = createSelector(
  selectFormTemplate,
  formTemplate => formTemplate?.formApplicationType,
);
